import React, { useState, useEffect } from 'react';
import { getAIConfig, setAIConfig, setDefaultProvider, verifyProvider } from '../../api/aiClient';
import type { AIProvider, AIProviderConfig, AIConfigStatus } from '@xiaotianshu/shared/ai';
import {
  getAutoUiScaleEnabled, getUiFontScale, setAutoUiScaleEnabled, setUiFontScale, UI_FONT_SCALE_OPTIONS,
} from '../../utils/uiScale';
import { BossKeySetting } from '../BossKeySetting';
import { UpdateSetting } from '../UpdateSetting';
import { isDesktopHost } from '../../utils/host';

interface APISettingsModalProps {
  /**
   * 「重置页面宽度」——按屏幕重算主窗体 / AI 面板 / 副窗口的宽度并应用。
   *
   * 桌面端才有效（要改原生窗口宽度）；网页端也允许点（会重置 AI 面板宽度），
   * 由调用方决定传不传。
   */
  onResetWidths?: () => void;
  onClose: () => void;
}

/** 设置分类 */
type SettingsSection = 'appearance' | 'password' | 'ai' | 'shortcut' | 'update';

/**
 * 桌面端才有"快捷键"（老板键要操作原生窗口；网页端显示它只会让人困惑），
 * 以及"软件更新"（只有桌面端有那个 exe 需要替换）。
 *
 * 用户要求：软件更新**单独列一项**，不要塞在"快捷键"里 —— 它俩是两件事。
 */
const SECTIONS: { id: SettingsSection; label: string }[] = [
  { id: 'appearance', label: '外观设置' },
  ...(isDesktopHost() ? [{ id: 'shortcut' as SettingsSection, label: '快捷键' }] : []),
  ...(isDesktopHost() ? [{ id: 'update' as SettingsSection, label: '软件更新' }] : []),
  { id: 'password', label: '访问密码' },
  { id: 'ai', label: 'AI 设置' },
];

/** 供应商有效性徽标 */
function ValidBadge({ valid }: { valid: boolean | null }) {
  if (valid === true) {
    return <span style={{ fontSize: 11, color: '#52c41a', border: '1px solid rgba(82,196,26,0.4)', borderRadius: 4, padding: '1px 6px' }}>已验证有效</span>;
  }
  if (valid === false) {
    return <span style={{ fontSize: 11, color: 'var(--danger-color)', border: '1px solid var(--danger-color)', borderRadius: 4, padding: '1px 6px' }}>验证无效</span>;
  }
  return <span style={{ fontSize: 11, color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: 4, padding: '1px 6px' }}>未验证</span>;
}

const PROVIDER_HELP: Record<AIProvider, { href: string; text: string } | null> = {
  deepseek: { href: 'https://platform.deepseek.com', text: 'platform.deepseek.com 获取' },
  openrouter: { href: 'https://openrouter.ai/keys', text: 'openrouter.ai/keys 获取' },
};

export function APISettingsModal({ onClose, onResetWidths }: APISettingsModalProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [activeSection, setActiveSection] = useState<SettingsSection>('appearance');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  // -- 外观设置：界面大小（叠在"随窗口自动缩放"之上的偏好档位） --
  const [uiScale, setUiScaleState] = useState(getUiFontScale());
  const [autoUiScale, setAutoUiScaleState] = useState(getAutoUiScaleEnabled());
  const handleUiScale = (v: number) => {
    setUiScaleState(v);
    setUiFontScale(v);
  };

  // -- AI 配置（多供应商，服务端存储，前端仅展示掩码） --
  const [status, setStatus] = useState<AIConfigStatus | null>(null);
  const [keyInputs, setKeyInputs] = useState<Record<AIProvider, string>>({ deepseek: '', openrouter: '' });
  const [aiSaving, setAiSaving] = useState<AIProvider | null>(null);
  const [verifyBusy, setVerifyBusy] = useState<AIProvider | null>(null);
  const [aiMsg, setAiMsg] = useState<Record<AIProvider, string>>({ deepseek: '', openrouter: '' });
  const [aiError, setAiError] = useState<Record<AIProvider, string>>({ deepseek: '', openrouter: '' });

  // -- 布局宽度不再在这里设置 --
  // 原先这里有一整套「区域布局」：填写左右区域的百分比、保存成命名组合。
  // 已移除 —— 宽度改为在创作页直接**拖拽**（各区域之间有拖拽手柄），
  // 并提供「恢复默认布局」按钮（图标栏底部）。理由见 `pages/Editor/paneLayout.ts`：
  // 百分比表达不了"右侧有 4 个可同时开启的面板"，且有固定像素上限会把超出的部分静默吃掉。

  useEffect(() => {
    getAIConfig().then(setStatus).catch(() => setStatus(null));
  }, []);

  const clearProvideMsg = (p: AIProvider) => {
    setAiMsg(prev => ({ ...prev, [p]: '' }));
    setAiError(prev => ({ ...prev, [p]: '' }));
  };

  const handleSaveAIConfig = async (p: AIProvider) => {
    const key = keyInputs[p].trim();
    if (!key) return;
    setAiSaving(p);
    clearProvideMsg(p);
    try {
      const s = await setAIConfig(key, { provider: p });
      setStatus(s);
      setKeyInputs(prev => ({ ...prev, [p]: '' }));
      try { localStorage.removeItem('api-config'); } catch { /* ignore */ }
      setAiMsg(prev => ({ ...prev, [p]: '配置已保存' }));
      window.dispatchEvent(new CustomEvent('ai-config-updated'));
    } catch (e) {
      setAiError(prev => ({ ...prev, [p]: e instanceof Error ? e.message : '保存失败' }));
    } finally {
      setAiSaving(null);
    }
  };

  const handleVerify = async (p: AIProvider) => {
    setVerifyBusy(p);
    clearProvideMsg(p);
    try {
      const { result, status: s } = await verifyProvider(p);
      setStatus(s);
      if (result.valid === true) {
        setAiMsg(prev => ({ ...prev, [p]: '验证成功，密钥有效' }));
      } else if (result.valid === false) {
        setAiError(prev => ({ ...prev, [p]: result.error || '密钥无效' }));
      } else {
        // 无法判定（网络错误等）：不更新已记录状态，提示稍后重试
        setAiError(prev => ({ ...prev, [p]: result.error || '暂时无法验证，请稍后重试' }));
      }
    } catch (e) {
      setAiError(prev => ({ ...prev, [p]: e instanceof Error ? e.message : '验证失败' }));
    } finally {
      setVerifyBusy(null);
    }
  };

  const handleMakeDefault = async (p: AIProvider) => {
    try {
      const s = await setDefaultProvider(p);
      setStatus(s);
      setAiMsg(prev => ({ ...prev, [p]: '已设为默认供应商' }));
    } catch (e) {
      setAiError(prev => ({ ...prev, [p]: e instanceof Error ? e.message : '设置失败' }));
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => { setIsClosing(false); onClose(); }, 300);
  };

  // Escape 关闭（与遮罩点击共用带动画的 handleClose）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isClosing) {
        e.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isClosing, handleClose]);

  const handleChangePassword = async () => {
    setPwdError('');
    setPwdSuccess('');
    if (!newPassword) { setPwdError('请输入新密码'); return; }
    if (newPassword.length < 4) { setPwdError('密码长度至少4位'); return; }
    if (newPassword !== confirmPassword) { setPwdError('两次输入的密码不一致'); return; }

    setPwdLoading(true);
    try {
      const result = await (window.api as any).changePassword(newPassword);
      if (result.success) {
        setPwdSuccess('密码修改成功');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPwdError(result.error || '修改失败');
      }
    } catch {
      setPwdError('修改失败，请重试');
    } finally {
      setPwdLoading(false);
    }
  };

  const inputStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
    width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)',
    borderRadius: 6, fontSize: 13, marginBottom: 8, boxSizing: 'border-box',
    backgroundColor: 'var(--card-bg)', color: 'var(--text-color)', ...extra,
  });

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, marginBottom: 6, color: 'var(--text-secondary)' };

  const providerCard = (p: AIProviderConfig) => {
    const busy = aiSaving === p.provider || verifyBusy === p.provider;
    const help = PROVIDER_HELP[p.provider];
    return (
      <div
        key={p.provider}
        style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</span>
          <ValidBadge valid={p.valid} />
          {p.isDefault
            ? <span style={{ fontSize: 11, color: 'var(--primary-color)', border: '1px solid var(--primary-color)', borderRadius: 4, padding: '1px 6px' }}>默认</span>
            : (
              <button
                onClick={() => void handleMakeDefault(p.provider)}
                disabled={busy}
                style={{
                  fontSize: 11, color: 'var(--primary-color)', border: '1px solid var(--primary-color)',
                  borderRadius: 4, padding: '1px 6px', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
                }}
              >
                设为默认
              </button>
            )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
          当前状态：{p.hasKey ? `已配置（${p.keyMask}）` : '未配置'} · 上游：{p.baseUrl}
        </div>
        <label style={labelStyle}>API Key</label>
        <input
          type="password"
          value={keyInputs[p.provider]}
          onChange={(e) => { setKeyInputs(prev => ({ ...prev, [p.provider]: e.target.value })); clearProvideMsg(p.provider); }}
          placeholder={p.hasKey ? '留空则不修改，输入以更新密钥' : `输入 ${p.label} API Key`}
          style={inputStyle({ marginBottom: p.isDefault ? 8 : 0 })}
          autoComplete="off"
        />
        {help && (
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
            从 <a href={help.href} target="_blank" rel="noreferrer" style={{ color: 'var(--primary-color)' }}>{help.text}</a>
          </p>
        )}
        {aiMsg[p.provider] && <div style={{ fontSize: 12, color: '#52c41a', marginBottom: 8 }}>{aiMsg[p.provider]}</div>}
        {aiError[p.provider] && <div style={{ fontSize: 12, color: 'var(--danger-color)', marginBottom: 8 }}>{aiError[p.provider]}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => void handleSaveAIConfig(p.provider)}
            disabled={busy}
            style={{
              padding: '8px 14px', backgroundColor: 'var(--primary-color)', color: 'white',
              border: 'none', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 500, opacity: busy ? 0.7 : 1,
            }}
          >
            {aiSaving === p.provider ? '保存中...' : '保存'}
          </button>
          <button
            onClick={() => void handleVerify(p.provider)}
            disabled={busy || !p.hasKey}
            title={!p.hasKey ? '请先配置 API Key' : '手动验证密钥有效性并更新状态'}
            style={{
              padding: '8px 14px', backgroundColor: 'transparent', color: 'var(--text-secondary)',
              border: '1px solid var(--border-color)', borderRadius: 6, cursor: busy || !p.hasKey ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 500, opacity: busy || !p.hasKey ? 0.6 : 1,
            }}
          >
            {verifyBusy === p.provider ? '验证中...' : '验证'}
          </button>
        </div>
      </div>
    );
  };

  const paneTitleStyle: React.CSSProperties = { fontSize: 15, fontWeight: 600, margin: '0 0 14px', color: 'var(--text-color)' };
  const paneDescStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 14px', lineHeight: 1.6 };

  const renderPane = () => {
    switch (activeSection) {
      case 'shortcut':
        return (
          <>
            <h3 style={paneTitleStyle}>快捷键</h3>
            <BossKeySetting />
          </>
        );

      case 'update':
        return (
          <>
            <h3 style={paneTitleStyle}>软件更新</h3>
            {/* 只替换那一个 portable exe —— 下载 / 校验 / 自替换的流程见 UpdateSetting 的说明 */}
            <UpdateSetting />
          </>
        );

      case 'appearance':
        return (
          <>
            <h3 style={paneTitleStyle}>外观设置</h3>
            {onResetWidths && (
              <>
                <p style={paneDescStyle}>
                  页面宽度：主窗体、AI 创作助手面板、右侧吸附窗口。首次打开软件时按屏幕算出一组默认值
                  （"主窗体 + 吸附窗口"略小于屏幕宽度）；你手动调过的宽度会被记住。
                  点下面的按钮可以重新计算并立即应用。
                </p>
                <button
                  type="button"
                  onClick={onResetWidths}
                  style={{
                    padding: '6px 16px', border: '1px solid var(--border-color)', borderRadius: 6,
                    cursor: 'pointer', fontSize: 13, fontWeight: 500, background: 'transparent',
                    color: 'var(--text-color)', marginBottom: 16,
                  }}
                >
                  重置页面宽度
                </button>
              </>
            )}
            <p style={paneDescStyle}>
              界面会**按窗口大小等比缩放**（窗体比例相同时，看到的版面相同）；
              下面这一档是叠在自动缩放之上的偏好，想整体更大/更小就调它。
              编辑器正文会跟着一起缩放，正文自身的字号仍由编辑工具栏的 A−/A+ 控制。
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {UI_FONT_SCALE_OPTIONS.map(o => (
                <button
                  key={o.value}
                  onClick={() => handleUiScale(o.value)}
                  style={{
                    padding: '6px 16px', border: '1px solid var(--border-color)', borderRadius: 6,
                    cursor: 'pointer', fontSize: 13, fontWeight: 500,
                    backgroundColor: uiScale === o.value ? 'var(--primary-color)' : 'transparent',
                    color: uiScale === o.value ? '#fff' : 'var(--text-color)',
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {/* 自动缩放的开关：留一个逃生口 —— 有人就喜欢"窗口小、字还是那么大" */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoUiScale}
                onChange={(e) => { setAutoUiScaleState(e.target.checked); setAutoUiScaleEnabled(e.target.checked); }}
              />
              随窗口大小自动缩放界面（推荐）
            </label>
          </>
        );
      case 'password':
        return (
          <>
            <h3 style={paneTitleStyle}>修改访问密码</h3>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPwdError(''); setPwdSuccess(''); }}
              placeholder="新密码（至少4位）"
              style={inputStyle()}
              autoComplete="new-password"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setPwdError(''); setPwdSuccess(''); }}
              placeholder="确认新密码"
              style={inputStyle()}
              autoComplete="new-password"
            />
            {pwdError && <div style={{ fontSize: 12, color: 'var(--danger-color)', marginBottom: 8 }}>{pwdError}</div>}
            {pwdSuccess && <div style={{ fontSize: 12, color: '#52c41a', marginBottom: 8 }}>{pwdSuccess}</div>}
            <button
              onClick={handleChangePassword}
              disabled={pwdLoading}
              style={{
                padding: '8px 16px', backgroundColor: 'var(--primary-color)', color: 'white',
                border: 'none', borderRadius: 6, cursor: pwdLoading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 500, opacity: pwdLoading ? 0.7 : 1
              }}
            >
              {pwdLoading ? '修改中...' : '修改密码'}
            </button>
          </>
        );
      case 'ai':
        return (
          <>
            <h3 style={paneTitleStyle}>AI 设置</h3>
            <p style={paneDescStyle}>
              配置各 AI 供应商的 API Key（密钥保存在服务端，不进入浏览器）。保存后可在「AI 对话」与「AI 助手」中自由选择供应商与模型。
            </p>
            {status ? status.providers.map(providerCard) : (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>加载配置中...</div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'var(--modal-mask)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 1000,
        animation: isClosing ? 'maskFadeOut 0.3s ease-out forwards' : 'maskFadeIn 0.3s ease-out'
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--card-bg)', borderRadius: 12, width: 660, maxWidth: '92vw',
          boxShadow: '0 4px 6px var(--shadow-color)', color: 'var(--text-color)',
          display: 'flex', flexDirection: 'column',
          /* 统一高度：不随分页内容多少变化；小屏时受视口限制 */
          height: 560,
          maxHeight: 'calc(100vh - 80px)',
          animation: isClosing ? 'modalFadeOut 0.3s ease-out forwards' : 'modalFadeIn 0.3s ease-out'
        }}>
        {/* 标题栏 */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', alignItems: 'center', flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>应用设置</h2>
        </div>

        {/* 主体：左侧分类标签 + 右侧分页内容 */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <div style={{
            width: 148, flexShrink: 0, borderRight: '1px solid var(--border-color)',
            padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 4,
            overflowY: 'auto', backgroundColor: 'var(--surface-bg)',
          }}>
            {SECTIONS.map(s => {
              const isActive = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 12px', border: 'none', borderRadius: 6, cursor: 'pointer',
                    backgroundColor: isActive ? 'var(--primary-color)' : 'transparent',
                    color: isActive ? '#fff' : 'var(--text-color)',
                    fontSize: 13, fontWeight: isActive ? 600 : 400,
                    transition: 'background-color 0.15s, color 0.15s',
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '20px 24px' }}>
            {renderPane()}
          </div>
        </div>

        {/* 底部：关闭 */}
        <div style={{
          borderTop: '1px solid var(--border-color)', padding: '12px 20px',
          display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '8px 16px', backgroundColor: 'transparent',
              color: 'var(--text-secondary)', border: '1px solid var(--border-color)',
              borderRadius: 4, cursor: 'pointer', fontSize: 13
            }}
          >
            关闭
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalFadeIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes modalFadeOut { from { transform: scale(1); opacity: 1; } to { transform: scale(0.95); opacity: 0; } }
        @keyframes maskFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes maskFadeOut { from { opacity: 1; } to { opacity: 0; } }
      `}</style>
    </div>
  );
}
